import React, { useEffect, useState, useContext, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { AuthContext } from '../../context/AuthContext';
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view';
import {
  FolderOpen as FolderOpenIcon,
  Person as PersonIcon,
  CorporateFare as BusinessIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { IconButton } from '@mui/material';
import Radio from '@mui/material/Radio';
import Checkbox from '@mui/material/Checkbox';
import './OrgTreeSelect.css';

// ✅ [고성능 최적화] 개별 사용자 행을 메모이제이션하여 불필요한 전체 리렌더링 방지
const UserRow = React.memo(({ user, isChecked, isUserSelectable, selectionMode, onAction, showDept = false }) => {
  const handlePointerDown = (e) => {
    e.preventDefault();
    if (isUserSelectable) onAction(user, true, !isChecked);
  };

  return (
    <tr
      className={isChecked ? 'selected' : ''}
      style={{ cursor: isUserSelectable ? 'pointer' : 'default', userSelect: 'none' }}
    >
      {isUserSelectable && (
        <td className="text-center" onPointerDown={handlePointerDown}>
          {selectionMode === 'single' ? (
            <Radio checked={isChecked} size="small" sx={{ padding: '2px', pointerEvents: 'none' }} />
          ) : (
            <Checkbox checked={isChecked} size="small" sx={{ padding: '2px', pointerEvents: 'none' }} />
          )}
        </td>
      )}
      <td onPointerDown={handlePointerDown}>{user.name}</td>
      <td onPointerDown={handlePointerDown}>{user.position}</td>
      <td onPointerDown={handlePointerDown}>{user.userid}</td>
      {showDept && (
        <td onPointerDown={handlePointerDown} style={{ fontSize: '11px', color: '#888' }}>
          {user.dept_name}
        </td>
      )}
    </tr>
  );
});

/**
 * 3단 프리미엄 OrgTreeSelect 컴포넌트
 */
export default function OrgTreeSelect({
  onConfirm,
  onCancel,
  selectionMode = "multiple",
  targetType = "all",
  initialSelected = [],
  comCode = ''
}) {
  const [deptTree, setDeptTree] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [allDeptCodes, setAllDeptCodes] = useState([]); // ✅ 전체 펼치기용 모든 부서 코드 저장
  const [expandedItems, setExpandedItems] = useState([]);

  const [deptSearchInput, setDeptSearchInput] = useState(''); // 부서 입력 필드값
  const [deptSearchTerm, setDeptSearchTerm] = useState('');   // 실제 필터링에 쓰일 값

  const [userSearchInput, setUserSearchInput] = useState(''); // 사용자 입력 필드값
  const [activeUserSearch, setActiveUserSearch] = useState(''); // 실제 필터링에 쓰일 사용자 검색어

  const [selectedTargets, setSelectedTargets] = useState(initialSelected);
  const [activeDept, setActiveDept] = useState(null);

  const { token, user } = useContext(AuthContext);

  const resolveComCode = useCallback(() => {
    const byProp = String(comCode || '').trim();
    if (byProp) return byProp;

    const byUser = String(user?.M_ComCode || '').trim();
    if (byUser) return byUser;

    try {
      if (!token) return '';
      const payload = token.split('.')[1];
      if (!payload) return '';
      const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
      return String(decoded?.M_ComCode || '').trim();
    } catch {
      return '';
    }
  }, [comCode, token, user]);

  useEffect(() => {
    const resolvedComCode = resolveComCode();
    const headers = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    if (resolvedComCode) headers['x-com-code'] = resolvedComCode;

    axios.get('/api/orgchart', {
      headers,
      params: resolvedComCode ? { M_ComCode: resolvedComCode } : undefined,
    })
      .then(res => {
        const { tree, allDeptCodes: codes, map, flatUsers } = buildDeptTree(res.data);
        setDeptTree(tree);
        setAllUsers(flatUsers);
        setAllDeptCodes(codes); // ✅ 모든 부서 코드 저장
        setExpandedItems(codes);

        let myDeptNode = null;
        if (user?.dept_code && map[user.dept_code]) {
          myDeptNode = map[user.dept_code];
        } else if (user?.userid) {
          const myDeptRaw = res.data.find(d => d.users?.some(u => String(u.userid) === String(user.userid)));
          if (myDeptRaw && map[myDeptRaw.dept_code]) {
            myDeptNode = map[myDeptRaw.dept_code];
          }
        }

        if (myDeptNode) {
          setActiveDept(myDeptNode);
        } else if (tree.length > 0) {
          setActiveDept(tree[0]);
        }
      })
      .catch(err => {
        console.error('조직도 불러오기 실패:', err);
      });
  }, [token, resolveComCode]);

  const buildDeptTree = (depts) => {
    const map = {};
    const tree = [];
    const allDeptCodes = [];
    const flatUsers = [];

    depts.forEach(dept => {
      allDeptCodes.push(String(dept.dept_code));
      const usersWithDept = (dept.users || []).map(u => {
        const userObj = {
          ...u,
          dept_code: dept.dept_code,
          dept_name: dept.dept_name,
          full_dept_name: dept.full_dept_name
        };
        flatUsers.push(userObj);
        return userObj;
      });
      map[dept.dept_code] = { ...dept, children: [], users: usersWithDept };
    });

    depts.forEach(dept => {
      if (dept.parent_dept_code && map[dept.parent_dept_code]) {
        map[dept.parent_dept_code].children.push(map[dept.dept_code]);
      } else {
        tree.push(map[dept.dept_code]);
      }
    });

    return { tree, allDeptCodes, map, flatUsers };
  };

  const handleCheck = useCallback((item, isUser, checked) => {
    const targetId = isUser ? String(item.userid) : String(item.dept_code);
    const targetType = isUser ? 'user' : 'dept';

    const targetObj = isUser
      ? {
        target_type: 'user',
        target_id: targetId,
        target_name: item.name,
        position: item.position || '',
        dept_name: item.dept_name || '',
        disp_target_name: `${item.name}${item.position ? " / " + item.position : ""}${item.dept_name ? " (" + item.dept_name + ")" : ""}`,
      }
      : {
        target_type: 'dept',
        target_id: targetId,
        target_name: item.dept_name,
        disp_target_name: item.full_dept_name || item.dept_name,
      };

    setSelectedTargets(prev => {
      if (selectionMode === "single") {
        return checked ? [targetObj] : [];
      } else {
        if (checked) {
          if (prev.some(t => t.target_id === targetId && t.target_type === targetType)) return prev;
          return [...prev, targetObj];
        } else {
          return prev.filter(t => !(t.target_id === targetId && t.target_type === targetType));
        }
      }
    });
  }, [selectionMode]);

  const removeTarget = (target_id, target_type) => {
    setSelectedTargets(prev => prev.filter(t => !(String(t.target_id) === String(target_id) && t.target_type === target_type)));
  };

  // 부서 검색 실행
  const runDeptSearch = () => {
    setDeptSearchTerm(deptSearchInput);
  };

  // 사용자 검색 실행
  const runUserSearch = () => {
    setActiveUserSearch(userSearchInput);
  };

  // 사용자 검색 초기화
  const clearUserSearch = () => {
    setUserSearchInput('');
    setActiveUserSearch('');
  };

  // 실제 표시할 사용자 목록 계산
  const displayUsers = useMemo(() => {
    if (!activeUserSearch) return activeDept?.users || [];
    const lowerSearch = activeUserSearch.toLowerCase();
    return allUsers.filter(u =>
      u.name.toLowerCase().includes(lowerSearch) ||
      String(u.userid).toLowerCase().includes(lowerSearch) ||
      (u.position && u.position.toLowerCase().includes(lowerSearch))
    );
  }, [activeUserSearch, activeDept, allUsers]);

  const renderTreeNodes = (nodes) => {
    return nodes.map(node => {
      const isDeptSelectable = targetType === "dept" || targetType === "all";
      const isChecked = selectedTargets.some(t => t.target_type === 'dept' && String(t.target_id) === String(node.dept_code));

      // 부서 검색 필터링
      if (deptSearchTerm && !node.dept_name.includes(deptSearchTerm)) {
        const hasMatchingChild = JSON.stringify(node.children).includes(deptSearchTerm);
        if (!hasMatchingChild) return null;
      }

      const labelContent = (
        <div
          style={{ display: 'flex', alignItems: 'center', padding: '4px 0', width: '100%' }}
          onClick={(e) => {
            e.stopPropagation();
            setActiveDept(node);
            clearUserSearch();
            // 부서 선택 전용 모드일 때만, 텍스트 클릭 시에도 선택 상태 토글
            if (targetType === 'dept') {
              handleCheck(node, false, !isChecked);
            }
          }}
        >
          {isDeptSelectable && (
            selectionMode === 'single' ? (
              <Radio
                checked={isChecked}
                onChange={(e) => handleCheck(node, false, e.target.checked)}
                onClick={(e) => e.stopPropagation()}
                size="small"
                sx={{ padding: '2px', marginRight: '4px' }}
              />
            ) : (
              <Checkbox
                checked={isChecked}
                onChange={(e) => handleCheck(node, false, e.target.checked)}
                onClick={(e) => e.stopPropagation()}
                size="small"
                sx={{ padding: '2px', marginRight: '4px' }}
              />
            )
          )}
          <FolderOpenIcon fontSize="small" sx={{ mr: 1, color: '#f6c23e' }} />
          <span style={{ fontWeight: activeDept?.dept_code === node.dept_code ? 'bold' : 'normal', color: '#333' }}>
            {node.dept_name}
          </span>
        </div>
      );

      return (
        <TreeItem key={String(node.dept_code)} itemId={String(node.dept_code)} label={labelContent}>
          {node.children?.length > 0 ? renderTreeNodes(node.children) : null}
        </TreeItem>
      );
    });
  };

  return ReactDOM.createPortal(
    <div className="org-tree-popup" onPointerDown={e => e.stopPropagation()}>
      <div className={`org-tree-popup-content ${targetType === 'dept' ? 'is-narrow' : ''}`}>
        <div className="org-tree-header">
          <h5>🏢 조직도 선택</h5>
          <IconButton onClick={onCancel} size="small"><CloseIcon /></IconButton>
        </div>

        <div className="org-tree-body">
          {/* 1. 트리 패널 (부서) */}
          <div className="org-tree-pane">
            <div className="org-tree-pane-header d-flex justify-content-between align-items-center">
              <span>조직 트리</span>
              <div className="d-flex gap-1">
                <button 
                  title="전체 펼치기"
                  className="btn btn-primary btn-xs p-0 d-flex align-items-center justify-content-center border-0 shadow-sm" 
                  style={{ width: '22px', height: '22px', fontSize: '14px', fontWeight: '900', borderRadius: '4px' }}
                  onClick={() => setExpandedItems(allDeptCodes)}
                >+</button>
                <button 
                  title="전체 접기"
                  className="btn btn-primary btn-xs p-0 d-flex align-items-center justify-content-center border-0 shadow-sm" 
                  style={{ width: '22px', height: '22px', fontSize: '14px', fontWeight: '900', borderRadius: '4px' }}
                  onClick={() => setExpandedItems([])}
                >-</button>
              </div>
            </div>
            <div className="p-2 border-bottom d-flex gap-1">
              <div style={{ position: 'relative', flex: 1 }}>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="부서명 검색..."
                  value={deptSearchInput}
                  onChange={(e) => setDeptSearchInput(e.target.value)}
                  onKeyPress={(e) => { if (e.key === 'Enter') runDeptSearch(); }}
                  style={{ paddingRight: deptSearchInput ? '30px' : '8px' }}
                />
                {deptSearchInput && (
                  <IconButton
                    size="small"
                    onClick={() => { setDeptSearchInput(''); setDeptSearchTerm(''); }}
                    style={{ position: 'absolute', right: '4px', top: '50%', transform: 'translateY(-50%)', padding: '2px' }}
                  >
                    <CloseIcon style={{ fontSize: '14px' }} />
                  </IconButton>
                )}
              </div>
              <button
                className="btn btn-outline-secondary btn-xs"
                style={{ padding: '0 8px', display: 'flex', alignItems: 'center' }}
                onClick={runDeptSearch}
              >
                <SearchIcon style={{ fontSize: '16px' }} />
              </button>
            </div>
            <div className="org-tree-pane-content premium-tree-root">
              <SimpleTreeView expandedItems={expandedItems} onExpandedItemsChange={(e, items) => setExpandedItems(items)}>
                {renderTreeNodes(deptTree)}
              </SimpleTreeView>
            </div>
          </div>

          {/* 2. 중앙 패널 (사용자 검색 및 목록) */}
          {targetType !== "dept" && (
            <div className="org-tree-pane">
              <div className="org-tree-pane-header">
                <span>👤 {activeUserSearch ? '전사 사용자 검색 결과' : (activeDept ? activeDept.dept_name : '부서원')}</span>
                <span className="badge bg-secondary">{displayUsers.length}명</span>
              </div>
              <div className="p-2 border-bottom d-flex gap-1">
                <div style={{ position: 'relative', flex: 1 }}>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="찾으실 사원 성명 입력..."
                    value={userSearchInput}
                    onChange={(e) => setUserSearchInput(e.target.value)}
                    onKeyPress={(e) => { if (e.key === 'Enter') runUserSearch(); }}
                    style={{ paddingRight: userSearchInput ? '30px' : '8px' }}
                  />
                  {userSearchInput && (
                    <IconButton
                      size="small"
                      onClick={clearUserSearch}
                      style={{ position: 'absolute', right: '4px', top: '50%', transform: 'translateY(-50%)', padding: '2px' }}
                    >
                      <CloseIcon style={{ fontSize: '14px' }} />
                    </IconButton>
                  )}
                </div>
                <button
                  className="btn btn-outline-secondary btn-xs"
                  style={{ padding: '0 10px', display: 'flex', alignItems: 'center' }}
                  onClick={runUserSearch}
                >
                  <SearchIcon style={{ fontSize: '16px' }} />
                </button>
              </div>
              <div className="org-tree-pane-content p-0">
                <table className="premium-user-table">
                  <thead>
                    <tr>
                      <th width="40" className="text-center">선택</th>
                      <th>이름</th>
                      <th>직책</th>
                      <th>사번</th>
                      {activeUserSearch && <th>부서</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {displayUsers.length > 0 ? (
                      displayUsers.map(user => (
                        <UserRow
                          key={user.userid}
                          user={user}
                          isChecked={selectedTargets.some(t => t.target_type === 'user' && t.target_id === String(user.userid))}
                          isUserSelectable={true}
                          selectionMode={selectionMode}
                          onAction={handleCheck}
                          showDept={!!activeUserSearch}
                        />
                      ))
                    ) : (
                      <tr><td colSpan="5" className="text-center text-muted py-4">조회된 사원이 없습니다.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* 3. 우측 패널 */}
          <div className="org-tree-pane">
            <div className="org-tree-pane-header bg-primary text-white d-flex justify-content-between align-items-center">
              <div>
                <span>✅ 선택된 대상</span>
                <span className="badge bg-light text-primary ms-2">{selectedTargets.length}</span>
              </div>
              <button type="button" className="btn btn-outline-light btn-xs py-0 px-2" style={{ fontSize: '11px', height: '22px' }} onClick={() => setSelectedTargets([])}>전체삭제</button>
            </div>
            <div className="org-tree-pane-content" style={{ backgroundColor: '#f8f9fa' }}>
              {selectedTargets.length > 0 ? (
                <ul className="selected-list">
                  {selectedTargets.map((t, idx) => (
                    <li key={`${t.target_type}-${t.target_id}-${idx}`} className="selected-item shadow-sm">
                      <div className="selected-info">
                        {t.target_type === 'dept' ? <BusinessIcon fontSize="small" sx={{ color: '#1cc88a' }} /> : <PersonIcon fontSize="small" sx={{ color: '#4e73df' }} />}
                        <div style={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
                          <div style={{ fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={t.target_name}>{t.target_name}</div>
                          <div style={{ fontSize: '11px', color: '#6c757d', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={t.disp_target_name}>{t.disp_target_name}</div>
                        </div>
                      </div>
                      <IconButton size="small" color="error" onClick={() => removeTarget(t.target_id, t.target_type)}><DeleteIcon fontSize="small" /></IconButton>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-center text-muted mt-5"><p>선택된 대상이 없습니다.</p></div>
              )}
            </div>
          </div>
        </div>

        <div className="org-tree-footer">
          <button className="btn btn-success px-4" onClick={() => { onConfirm(selectedTargets); onCancel(); }}>확인</button>
          <button className="btn btn-secondary px-4" onClick={onCancel}>취소</button>
        </div>
      </div>
    </div>,
    document.body
  );
}